
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { LearningPath } from '@/types/learning';
import { getLearningPaths } from '@/services/learning-service';
import { useAuth } from '@/contexts/AuthContext';
import { ContentSeeder } from '@/components/admin/ContentSeeder';

const LearningPaths = () => {
  const [paths, setPaths] = useState<LearningPath[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);

  useEffect(() => {
    const fetchPaths = async () => {
      try {
        const data = await getLearningPaths();
        setPaths(data);
      } catch (error) {
        console.error('Error fetching learning paths:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaths();
  }, []);

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6">
        <header className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold font-lexend">Learning Paths</h1>
            <button 
              onClick={() => setShowAdmin(!showAdmin)} 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {showAdmin ? "Hide Admin" : "Admin"}
            </button>
          </div>
          <p className="text-muted-foreground">
            Structured learning journeys to help you master AI concepts and skills
          </p>
        </header>

        {showAdmin && (
          <div className="mb-8">
            <ContentSeeder />
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-36 bg-muted rounded-t-lg" />
                <div className="p-4 space-y-3 border border-t-0 rounded-b-lg">
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-20 bg-muted rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paths.map((path) => (
              <Link 
                key={path.id} 
                to={`/learning-paths/${path.id}`}
                className="no-underline"
              >
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="overflow-hidden h-full border-2 hover:border-primary/50 transition-colors">
                    <div 
                      className="h-36 bg-gradient-to-r from-primary/30 to-primary/10 flex items-center justify-center"
                    >
                      <span className="text-4xl font-bold">{path.title.substring(0, 2)}</span>
                    </div>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-xl font-bold">{path.title}</CardTitle>
                        <Badge variant="outline" className="ml-2">
                          {path.difficulty}
                        </Badge>
                      </div>
                      <CardDescription>
                        {path.estimatedHours} hours • {path.modules.length || 0} modules
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {path.description}
                      </p>
                      {path.progress !== undefined && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Your progress</span>
                            <span>{path.progress}%</span>
                          </div>
                          <Progress value={path.progress} className="h-2" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default LearningPaths;
